// useHashFilters — persist filter state in the URL hash query string so
// refreshes don't reset the view.
//
// The app uses HashRouter, so the path lives in window.location.hash
// (e.g. '#/ledger'). Filter state appends as a query string after a `?`:
//
//   #/ledger?proc.q=Hoback&proc.proj=26-Hoback&proc.unpaid=1
//
// Namespace the keys (`proc.*`, `bs.*`, `recv.*`) so independent sub-views
// can each own their filters without colliding. Boolean values encode as
// '1' (true) / dropped (false), and default values are stripped on write
// to keep URLs clean.
//
// Usage:
//   var f = useHashFilters({
//     namespace: 'proc',
//     defaults:  { q: '', proj: '', unpaid: false },
//   });
//   var filters = f[0];     // { q, proj, unpaid }
//   var setFilters = f[1];  // full setter (function form supported)
//   var setFilter = f[2];   // setFilter('q', 'Hoback')
//
// Coexistence with the QB OAuth callback handler in Ledger.jsx: that
// handler runs once on mount and strips its own params (qb, realmId,
// env) along with everything else after `?`. The filter sub-view mounts
// AFTER the OAuth strip, so by the time useHashFilters reads the hash,
// the OAuth params are already gone. Active filters during an OAuth
// round-trip reset to defaults — acceptable edge case; the alternative
// (selective param-stripping in the OAuth handler) is out of scope here.

import { useState, useEffect } from 'react';

function readHash() {
  if (typeof window === 'undefined') return {};
  var hash = window.location.hash || '';
  var qIdx = hash.indexOf('?');
  if (qIdx < 0) return {};
  var qs = hash.slice(qIdx + 1);
  var params = new URLSearchParams(qs);
  var obj = {};
  params.forEach(function(v, k) { obj[k] = v; });
  return obj;
}

function writeHash(params) {
  if (typeof window === 'undefined') return;
  var hash = window.location.hash || '';
  var qIdx = hash.indexOf('?');
  var base = qIdx < 0 ? hash : hash.slice(0, qIdx);
  var keys = Object.keys(params).filter(function(k) { return params[k] != null && params[k] !== ''; });
  var newHash;
  if (keys.length === 0) {
    newHash = base;
  } else {
    var qs = keys.map(function(k) {
      return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
    }).join('&');
    newHash = base + '?' + qs;
  }
  if (window.location.hash === newHash) return;
  var newUrl = window.location.pathname + window.location.search + newHash;
  window.history.replaceState({}, '', newUrl);
}

export function useHashFilters(opts) {
  opts = opts || {};
  var namespace = opts.namespace || '';
  var defaults = opts.defaults || {};

  function hashKey(k) { return namespace ? namespace + '.' + k : k; }

  function readFromHash() {
    var hp = readHash();
    var out = Object.assign({}, defaults);
    Object.keys(defaults).forEach(function(k) {
      var raw = hp[hashKey(k)];
      if (raw == null) return;
      if (typeof defaults[k] === 'boolean') out[k] = raw === '1' || raw === 'true';
      else out[k] = raw;
    });
    return out;
  }

  var fs = useState(readFromHash);
  var filters = fs[0];
  var setFiltersRaw = fs[1];

  useEffect(function() {
    var hp = readHash();
    Object.keys(defaults).forEach(function(k) {
      var v = filters[k];
      var def = defaults[k];
      var drop = v == null || v === '' || v === def;
      if (typeof def === 'boolean' && v === false) drop = true;
      if (drop) delete hp[hashKey(k)];
      else hp[hashKey(k)] = typeof v === 'boolean' ? '1' : String(v);
    });
    writeHash(hp);
  }, [JSON.stringify(filters)]);

  function setFilter(k, v) {
    setFiltersRaw(function(prev) {
      var n = Object.assign({}, prev);
      n[k] = v;
      return n;
    });
  }

  return [filters, setFiltersRaw, setFilter];
}
